<script setup lang="ts">
import { $getCampaignSimpleList } from '@/api/campaigns'
import { uniqBy } from 'lodash'

interface IProps {
  modelValue?: any,
  label?: string,
  type?: any,
  eventGroup?: any,
  supplier?: any,
  selectOptions?: any[] | null,
  /**
   * Optional array of campaign IDs that should be disabled for selection.
   * If null or undefined, no campaigns will be disabled.
   */
  disabledCampaigns?: number[] | null,
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: () => {},
  label: 'Project',
  type: null,
  supplier: null,
  eventGroup: null,
  selectOptions: null,
  disabledEventGroups: null,
})

interface Emit {
  (e: 'update:modelValue', value: any): void
}

const emit = defineEmits<Emit>()

interface CampaignSelect {
  id: number
  name: string
}

const campaigns = ref<CampaignSelect[]>([])
const campaignsCurrentPage = ref<number>(1)
const campaignsPerPage: number = 10
const campaignsSearchStr = ref<string>('')
const isNextCampaignsPageExist = ref<boolean>(false)
const campaignsLoading = ref<boolean>(false)

const campaignOptions = computed(() => {
  if (props.selectOptions) {
    return props.selectOptions
  } else {
    return campaigns.value
  }
})

const getCampaigns = async () => {
  campaignsLoading.value = true

  const filters = {
    // Minimum number of fields('id', 'name')
    is_select: true,
    type: props.type, // 1 - Wayfinding, 2 - Branding
    supplier: props.supplier ? props.supplier : undefined,
    is_visible: true,
    // Filter by Event Group(Program)
    event_group_or_children: props.eventGroup && props.eventGroup.id ? props.eventGroup.id : undefined,

    search: campaignsSearchStr.value,
    per_page: campaignsPerPage,
    page: campaignsCurrentPage.value,
  }
  try {
    const { data, count, isNextPageExist, error, loading } = await $getCampaignSimpleList(filters)

    // Append the new campaigns to the existing list
    // Use Lodash's uniqBy to merge arrays while ensuring uniqueness based on 'id'
    campaigns.value = uniqBy([...campaigns.value, ...data.value], 'id')

    isNextCampaignsPageExist.value = !!isNextPageExist.value
  }
  catch (e) {
    console.log(e)
  }
  finally {
    campaignsLoading.value = false
  }
}

const onFocusCampaignSelect = async () => {
  // Check if eventGroups.value is empty and either props.modelValue is falsy or props.selectOptions is falsy
  if (!campaigns.value.length &&
    (props.modelValue === undefined || props.modelValue === null) &&
    !props.selectOptions
  ) {
    campaigns.value = []
    campaignsCurrentPage.value = 1
    await getCampaigns()
  }
}

const onLoadMoreCampaigns = async () => {
  campaignsCurrentPage.value++ // Increment the current page to load the next page of campaigns
  await getCampaigns()
}

const handleCampaignSearch = useDebounceFn((searchStr: string) => {
  if (!props.selectOptions) {
    campaigns.value = []
    campaignsCurrentPage.value = 1 // Set current page => 1 to load initial first page of locations
    campaignsSearchStr.value = searchStr
    getCampaigns()
  }
}, 500)

watch(() => props.type, async () => {
  campaigns.value = []
  campaignsCurrentPage.value = 1
  await getCampaigns()
})

watch(() => props.eventGroup, async () => {
  campaigns.value = []
  campaignsCurrentPage.value = 1
  await getCampaigns()
})
</script>

<template>
  <VAutocomplete
    :model-value="modelValue"
    :items="[...campaignOptions]"
    return-object
    item-title="name"
    item-value="id"
    :label="label"
    :no-filter="!selectOptions"
    density="compact"
    no-filter
    clearable
    @update:modelValue="newValue => $emit('update:modelValue', newValue)"
    @update:focused="onFocusCampaignSelect"
    @update:search="handleCampaignSearch"
  >
    <template
      v-if="disabledCampaigns"
      #item="{ item, index, props }"
    >
      <VListItem
        v-bind="props"
        :disabled="!!disabledCampaigns ? disabledCampaigns.includes(item?.value) : false"
      />
    </template>
    <template #no-data>
      <div
        v-if="!!campaignsLoading"
        class="d-flex justify-center align-center"
      >
        Loading...
        <VProgressCircular
          class="margin-left-5"
          size="22"
          indeterminate
          color="white"
        />
      </div>
      <div
        v-else
        class="d-flex justify-center align-center"
      >
        Projects not found
      </div>
    </template>
    <template
      v-if="isNextCampaignsPageExist"
      #append-item
    >
      <VBtn
        color="default"
        block
        :disabled="!!campaignsLoading"
        @click="onLoadMoreCampaigns"
      >
        Load more...
        <VProgressCircular
          v-if="!!campaignsLoading"
          class="margin-left-5"
          size="22"
          indeterminate
          color="white"
        />
      </VBtn>
    </template>
  </VAutocomplete>
</template>
